import { AsyncData } from '../core';

export interface ClaimsState {
  claims: AsyncData<Claim[]>;
}

export interface ClaimsServerResponse {
  content: Claim[];
  pageable: Pageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  sort: Sort;
  numberOfElements: number;
  size: number;
  number: number;
}

export interface Pageable {
  sort: Sort;
  pageSize: number;
  pageNumber: number;
  offset: number;
  unpaged: boolean;
  paged: boolean;
}

export interface Sort {
  sorted: boolean;
  unsorted: boolean;
}

export enum State {
  Draft = 'Draft',
  ValidationFailed = 'ValidationFailed',
  Submitted = 'Submitted',
  Acknowledged = 'Acknowledged',
  Adjudication = 'Adjudication',
  MoreInfoRequired = 'MoreInfoRequired',
  Rejected = 'Rejected',
  PayerError = 'PayerError',
  Finalized = 'Finalized',
  Replaced = 'Replaced',
  Unknown = 'Unknown',
}

export interface Address {
  streetLine_1: string;
  streetLine_2?: string;
  city: string;
  state: string;
  zip: string;
}

export interface Payer {
  id: string;
  name: string;
  address: Address;
  code: string;
}

export interface Demographic {
  firstName: string;
  lastName: string;
  middleName?: string;
  memberId: string;
  groupId: string;
  groupName?: string;
  dob: string;
  gender?: string;
  phone: string;
  relationship?: string;
  relationshipCode?: string;
  address: Address;
}

export interface BillingProvider {
  id: number;
  npi: string;
  firstName?: string;
  lastName?: string;
  description: string;
  taxId: string;
  address: Address;
  taxonomyCode: string;
  phoneNumber: string;
  organizationName: string;
  entity: boolean;
  statesOfOperation: string[];
}

export interface Provider {
  id?: string;
  npi: string;
  firstName: string;
  lastName: string;
}

export interface Adjustment {
  typeCode: string;
  reasonCode: string;
  amount: number;
  quantity: number;
}

export interface Remark {
  typeCode: string;
  value: string;
}

export interface ServiceLine {
  start: string;
  end: string;
  placeOfService: string;
  renderingProvider: Provider;
  cptCode: string;
  qualifier?: any;
  modifiers: string[];
  units: number;
  icdPointers: number[];
  billedAmount: number;
  paidAmount?: number;
  allowedAmount?: number;
  status?: PayerStatus;
  adjustments?: Adjustment[];
  remarkCodes?: Remark[];
}

export interface PayerStatus {
  categoryCode: string;
  statusCode: string;
}

export interface Claim {
  id: string;
  revision: number;
  externalReferenceId: string;
  state: string;
  payerStatus?: PayerStatus;
  dateOfService: Date;
  lastClaimMessageUpdate?: string;
  lastRemittanceAdviceUpdate?: string;
  lastPaymentStatusUpdate?: string;
  lastPayerStatusUpdate?: string;
  createdAt: string;
  updatedAt: string;
  submittedDate: string;
  payerControlNumber?: string;
  processorId: string;
  processor: string;
  frequency: string;
  payer: Payer;
  subscriber: Demographic;
  dependent: Demographic;
  billingProvider: BillingProvider;
  icdCodes: string[];
  serviceLines: ServiceLine[];
  billedAmount: number;
  amountPatientPaid: number;
  payerPaid?: number;
  patientResponsibility?: number;
  totalCoverage?: number;
  adjustments?: Adjustment[];
  validationErrors?: string[];
  icdMode: string;
  costEstimationId?: string;
  metadata?: ClaimMetadata;
  renderingProvider: Provider;
  supervisingProvider: Provider;
  placeOfServiceAddress: {
    state: string;
  };
}

export interface ClaimMetadata {
  visit_symptoms?: string;
  service_code: string;
}

export interface ClaimEvaluationRequest {
  visitReference: string;
  forceFinalizedClaimReplacement: boolean;
  skipOldDataCheck: boolean;
  preventSubmission: boolean;
}

export interface ClaimEvaluationResponse {
  operation: string;
  shouldSubmitToPayers: boolean;
  compiledClaim?: Claim;
}
